import PhotoCropDialog from "../components/hris/PhotoCropDialog";
import { useEffect, useRef, useState } from "react";
import { api, API, gantiPasswordSendiri } from "../lib/api";
import { KeyRound, FileUp, Trash2, Download, FileText, Loader2, Camera } from "lucide-react";
import { toast } from "sonner";
import AuthenticatorSection from "../components/AuthenticatorSection";
import { ATURAN_PASSWORD, periksaPasswordBaru } from "../lib/password";
import EmployeeAvatar from "../components/hris/EmployeeAvatar";
import { prepareEmployeePhoto, notifyEmployeePhotoChanged } from "../lib/employeePhoto";

export default function MyProfile() {
  const [tab, setTab] = useState("photo");
  const [pw, setPw] = useState({ current_password: "", new_password: "", confirm: "" });
  // Naik tiap ganti password berhasil → AuthenticatorSection dipasang ulang dan
  // membaca status barunya (penyambungan terbuka begitu password sementara diganti).
  const [versiPw, setVersiPw] = useState(0);
  const [docs, setDocs] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [docName, setDocName] = useState("KTP");
  const [profile, setProfile] = useState(null);
  const [photoStatus, setPhotoStatus] = useState("");
  const photoBusy = useRef(false);
  const [photoFile, setPhotoFile] = useState(null);

  const loadProfile = () => api.get("/my/profile").then(r => setProfile(r.data));
  useEffect(() => { api.get("/my/documents").then(r => setDocs(r.data)); loadProfile(); }, []);

  const changePw = async (e) => {
    e.preventDefault();
    if (pw.new_password !== pw.confirm) { toast.error("Konfirmasi password tidak cocok"); return; }
    const galat = periksaPasswordBaru(pw.new_password);
    if (galat) { toast.error(galat); return; }
    try {
      // Perangkat lain milik akun ini dikeluarkan server; perangkat ini diberi
      // token baru (disimpan `gantiPasswordSendiri`) sehingga tetap masuk.
      await gantiPasswordSendiri("/auth/change-password", { current_password: pw.current_password, new_password: pw.new_password });
      toast.success("Password berhasil diganti. Perangkat lain yang memakai akun ini sudah dikeluarkan.");
      setPw({ current_password: "", new_password: "", confirm: "" });
      setVersiPw(v => v + 1);
    } catch (err) {
      // Password lama salah = 400 (dulu 401 → interceptor mengeluarkan pengguna).
      toast.error(err?.response?.data?.detail || "Gagal");
    }
  };

  const uploadPhoto = async (file, crop) => {
    if (!file || photoBusy.current || !profile?.employee?.id) return;
    photoBusy.current = true;
    setPhotoStatus("Menyiapkan foto...");
    try {
      const photo = await prepareEmployeePhoto(file, crop);
      setPhotoStatus("Mengunggah foto...");
      await api.post("/my/photo", { content_base64: photo.content_base64 });
      notifyEmployeePhotoChanged(profile.employee.id);
      toast.success(`Foto profil diperbarui (${Math.ceil(photo.bytes / 1024)} KB).`);
      return true;
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Gagal mengunggah foto.");
      return false;
    } finally { photoBusy.current = false; setPhotoStatus(""); }
  };

  const deletePhoto = async () => {
    if (photoBusy.current || !profile?.employee?.id) return;
    if (!confirm("Hapus foto profil?")) return;
    photoBusy.current = true; setPhotoStatus("Menghapus foto...");
    try { await api.delete("/my/photo"); toast.success("Foto dihapus"); notifyEmployeePhotoChanged(profile.employee.id); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
    finally { photoBusy.current = false; setPhotoStatus(""); }
  };

  const uploadFile = async (file) => {
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast.error("File maks 2MB"); return; }
    setUploading(true);
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        await api.post("/my/documents", { nama: docName, file_name: file.name, content_base64: reader.result, mime_type: file.type || "application/octet-stream" });
        toast.success("Dokumen ter-upload");
        const { data } = await api.get("/my/documents"); setDocs(data);
      } catch (e) { toast.error(e?.response?.data?.detail || "Gagal upload"); }
      setUploading(false);
    };
    reader.readAsDataURL(file);
  };

  const download = async (id, fname) => {
    const token = localStorage.getItem("hris_token");
    const res = await fetch(`${API}/my/documents/${id}/download`, { headers: { Authorization: `Bearer ${token}` } });
    const blob = await res.blob(); const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = fname;
    document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
  };

  const del = async (id) => {
    if (!confirm("Hapus dokumen?")) return;
    await api.delete(`/my/documents/${id}`);
    const { data } = await api.get("/my/documents"); setDocs(data);
  };

  const empId = profile?.employee?.id;
  const nama = profile?.employee?.nama;

  return (
    <div className="p-4 md:p-8 max-w-[1000px]">
      {photoFile && <PhotoCropDialog file={photoFile} onCancel={() => setPhotoFile(null)} onConfirm={crop => uploadPhoto(photoFile, crop)} />}
      <header className="mb-8">
        <p className="text-xs font-semibold uppercase tracking-wider text-emerald-600 mb-2">Akun</p>
        <h1 className="font-display text-2xl md:text-3xl font-bold text-slate-900">Pengaturan Saya</h1>
        <p className="mt-2 text-sm text-slate-500">Kelola foto profil, password, dan dokumen pribadi Anda.</p>
      </header>

      <div className="flex gap-2 mb-6 border-b border-slate-200">
        {[["photo", "Foto Profil"], ["password", "Password & Keamanan"], ["docs", "Dokumen"]].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`tab-${k}`}
            className={`px-4 h-10 border-b-2 -mb-px text-sm font-medium transition-colors ${tab === k ? "border-emerald-600 text-emerald-700" : "border-transparent text-slate-500 hover:text-slate-900"}`}>{l}</button>
        ))}
      </div>

      {tab === "photo" && (
        <div className="bg-white border border-slate-200 rounded-xl p-8 shadow-sm max-w-xl">
          <div className="flex flex-col sm:flex-row items-center gap-6">
            <EmployeeAvatar employeeId={empId} nama={nama} size={112} className="shrink-0 border-4 border-white shadow-lg" />
            <div className="flex-1">
              <h3 className="font-display font-semibold text-slate-900 mb-1">Foto Profil</h3>
              <p className="text-xs text-slate-500 mb-4">Ganti foto akunmu di sini. Foto ukuran besar otomatis dikompres sebelum diunggah, lalu tampil di sidebar, dashboard, dan kartu karyawan.</p>
              <div className="flex gap-2">
                <label data-testid="btn-upload-photo" aria-disabled={!!photoStatus || !empId} className={`relative inline-flex items-center gap-2 min-h-10 px-4 rounded-lg bg-[#059669] hover:bg-[#047857] text-white text-sm font-medium cursor-pointer transition-colors ${photoStatus || !empId ? "opacity-50 cursor-wait" : ""}`}>
                  {photoStatus ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />} Ganti Foto
                  <input type="file" accept="image/*" disabled={!!photoStatus || !empId} className="absolute inset-0 w-full h-full opacity-0 cursor-pointer" aria-label="Pilih foto profil"
                    onChange={e => { const file = e.target.files?.[0]; e.target.value = ""; if (file) setPhotoFile(file); }} data-testid="photo-file" />
                </label>
                <button onClick={deletePhoto} data-testid="btn-del-photo" disabled={!!photoStatus || !empId}
                  className="inline-flex items-center gap-2 h-10 px-4 rounded-lg border border-slate-200 text-sm text-slate-700 hover:bg-slate-50">
                  <Trash2 className="w-4 h-4" /> Hapus
                </button>
              </div>
              <p role="status" className="text-xs text-slate-500 mt-3" data-testid="photo-upload-status">{photoStatus}</p>
            </div>
          </div>
        </div>
      )}

      {tab === "password" && (
        <div className="space-y-6">
        <form onSubmit={changePw} className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm max-w-lg space-y-4">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-lg bg-emerald-50 text-emerald-600 grid place-items-center"><KeyRound className="w-5 h-5" /></div>
            <h3 className="font-display font-semibold text-slate-900">Ganti Password</h3>
          </div>
          {[["current_password", "Password Lama"], ["new_password", "Password Baru"], ["confirm", "Konfirmasi Password Baru"]].map(([k, l]) => (
            <label key={k} className="block">
              <span className="text-xs font-medium text-slate-600">{l}</span>
              <input type="password" required value={pw[k]} onChange={e => setPw(s => ({ ...s, [k]: e.target.value }))}
                data-testid={`pw-${k}`} autoComplete={k === "current_password" ? "current-password" : "new-password"}
                className="mt-1 w-full h-10 px-3 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-600/40" />
              {k === "new_password" && (
                <span className="block text-[11px] text-slate-500 mt-1" data-testid="aturan-password">{ATURAN_PASSWORD}</span>
              )}
            </label>
          ))}
          <button type="submit" data-testid="btn-change-pw"
            className="h-10 px-5 rounded-lg bg-[#059669] hover:bg-[#047857] text-white text-sm font-medium transition-colors">Simpan</button>
        </form>
        {/* Google Authenticator (keputusan owner 24 Sep 2026): jalur reset
            password mandiri dari halaman login. */}
        <AuthenticatorSection key={versiPw} />
        </div>
      )}

      {tab === "docs" && (
        <div className="space-y-6">
          <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm">
            <h3 className="font-display font-semibold text-slate-900 mb-4">Upload Dokumen Baru</h3>
            <div className="flex flex-wrap items-end gap-3">
              <label className="block">
                <span className="text-xs font-medium text-slate-600">Jenis Dokumen</span>
                <select value={docName} onChange={e => setDocName(e.target.value)} data-testid="doc-name"
                  className="mt-1 h-10 px-3 rounded-lg border border-slate-200 text-sm bg-white min-w-[180px]">
                  {["KTP", "NPWP", "BPJS Kesehatan", "BPJS Ketenagakerjaan", "Ijazah", "Sertifikat", "Kartu Keluarga", "Lainnya"].map(x => <option key={x}>{x}</option>)}
                </select>
              </label>
              <label className="inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-[#059669] hover:bg-[#047857] text-white text-sm font-medium cursor-pointer transition-colors">
                {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileUp className="w-4 h-4" />}
                {uploading ? "Uploading..." : "Pilih File (max 2MB)"}
                <input type="file" accept="image/*,application/pdf" className="hidden" disabled={uploading}
                  onChange={e => uploadFile(e.target.files?.[0])} data-testid="doc-file" />
              </label>
              <span className="text-xs text-slate-500">Format: JPG, PNG, PDF</span>
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="bg-slate-50 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                <th className="text-left py-3 px-4">Jenis</th><th className="text-left py-3 px-4">Nama File</th>
                <th className="text-right py-3 px-4">Size</th><th className="text-left py-3 px-4">Upload</th>
                <th className="text-right py-3 px-4">Aksi</th>
              </tr></thead>
              <tbody>
                {docs.map(d => (
                  <tr key={d.id} className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
                    <td className="py-3 px-4"><span className="px-2 py-0.5 rounded-full text-xs bg-emerald-100 text-emerald-800">{d.nama}</span></td>
                    <td className="py-3 px-4 text-slate-700">{d.file_name}</td>
                    <td className="py-3 px-4 text-right text-slate-500 text-xs">{(d.size_bytes / 1024).toFixed(1)} KB</td>
                    <td className="py-3 px-4 text-slate-500 text-xs">{d.created_at?.slice(0, 10)}</td>
                    <td className="py-3 px-4 text-right">
                      <button onClick={() => download(d.id, d.file_name)} className="p-2 hover:bg-slate-100 rounded-lg text-slate-500 hover:text-slate-900"><Download className="w-4 h-4" /></button>
                      <button onClick={() => del(d.id)} className="p-2 hover:bg-rose-50 rounded-lg text-slate-400 hover:text-rose-600"><Trash2 className="w-4 h-4" /></button>
                    </td>
                  </tr>
                ))}
                {docs.length === 0 && <tr><td colSpan={5} className="text-center py-10 text-sm text-slate-500"><FileText className="w-8 h-8 text-slate-300 mx-auto mb-2" />Belum ada dokumen</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
